<template>
  <the-row v-bind:columns="columns">
    <div
      slot="service"
      class="form-invoice__table__cell name"
      data-label="Услуга"
      v-bind:title="initialCategoryName">
      <v-autocomplete
        v-if="!disabled"
        class="form-invoice__table__input"
        append-icon=""
        attach
        hide-details="auto"
        v-bind:error-messages="inputErrors"
        placeholder="Введите название услуги"
        ref="serviceSelector"
        required="required"
        v-bind:disabled="disabled"
        v-bind:full-width="false"
        item-text="name"
        item-value="id"
        v-bind:items="services"
        v-bind:value="initialServiceId"
        v-on:input="onInputServiceId"
        no-data-text="Результатов нет"
      />
      <p
        v-else
        class="form-invoice__table__service-name">
        {{ initialServiceName }}
      </p>
    </div>
    <div
      slot="clinic_code"
      class="form-invoice__table__cell"
      data-label="Код клиники"
    >
      {{ initialServiceClinicCode }}
    </div>
    <div
      slot="dental_code"
      class="form-invoice__table__cell"
      data-label="Код номенклатуры"
    >
      {{ initialServiceDentalCode }}
    </div>
    <div slot="cost" class="form-invoice__table__cell number" data-label="Цена">
      <the-rub v-bind:kop="initialCost" show-kop="auto" currency="тнг" />
    </div>
    <div slot="count" class="form-invoice__table__cell number count" data-label="Кол-во">
      <input-count
        v-bind:disabled="disabled"
        v-bind:value="initialCount"
        v-on:input="onInputCount"
      />
    </div>
    <div
      slot="discount"
      class="form-invoice__table__cell number discount"
      data-label="Скидка"
    >
      <input-discount
        v-bind:disabled="disabled"
        v-bind:discount-type="initialDiscountType"
        v-bind:max-discount-in-kop="amountInKop"
        v-bind:value="initialDiscount"
        v-bind:show-discount-postfix="showDiscountPostfix"
        v-on:input="onInputDiscount"
      />
    </div>
    <div
      v-if="showComplaintCell"
      slot="complaint"
      class="form-invoice__table__cell checkbox"
      data-label="Рекламация"
    >
      <input-checkbox
        v-if="!disabled || initialComplaint"
        v-bind:disabled="disabled"
        v-bind:value="initialComplaint"
        v-on:input="onInputComplaint"
      />
    </div>
    <div slot="final_price" class="form-invoice__table__cell number" data-label="Итого">
      <the-rub v-bind:kop="initialFinalPrice" show-kop="auto" currency="тнг" />
    </div>
    <div slot="status" class="form-invoice__table__cell status" data-label="Статус">{{ status }}</div>
    <div
      v-if="!disabled"
      slot="remove"
      class="form-invoice__table__cell remove"
      data-label="Удалить">
      <button
        class="form-invoice__icons__remove"
        type="button"
        v-on:click="emitRemove"
      />
    </div>
  </the-row>
</template>

<script>
import InputCheckbox from '@/components/InputCheckbox.vue';
import InputCount from '@/components/InputCount.vue';
import InputDiscount from '@/components/InputDiscount.vue';
import TheRub from '@/components/TheRub.vue';

const TheRow = {
  functional: true,
  name: 'TheRow',
  props: {
    columns: { required: true, type: Array },
  },
  render(createElement, { props, scopedSlots }) {
    return createElement(
      'div',
      [
        ...props.columns.map((column) => scopedSlots[column.id]()),
        scopedSlots.complaint && scopedSlots.complaint(),
        scopedSlots.remove && scopedSlots.remove(),
      ],
    );
  },
};

/**
 * @description Название события удаления позиции.
 * @type {string}
 */
const NAME_EVENT_REMOVE = 'remove';

/**
 * @description Компонент строки позиции счета.
 * @type {any}
 */
export default {
  components: {
    InputCheckbox,
    InputCount,
    InputDiscount,
    TheRow,
    TheRub,
  },
  computed: {
    /**
     * @description Деактивировать поле ДМС.
     * @returns {boolean}
     */
    isByVHIPolicyDisabled() {
      return this.disabled || this.initialComplaint;
    },
  },
  name: 'RowInvoice',
  methods: {
    /**
     * @description Послать событие удаления позиции.
     * @returns {void}
     */
    emitRemove() {
      this.$emit(NAME_EVENT_REMOVE);
    },
    onInputDiscount(value) {
      this.$emit('input-discount', value);
    },
    onInputCount(value) {
      this.$emit('input-count', value);
    },
    onInputByVhiPolicy(value) {
      this.$emit('input-by-vhi-policy', value);
    },
    onInputComplaint(value) {
      this.$emit('input-complaint', value);
    },
    onInputServiceId(value) {
      this.$emit('input-service-id', value);
    },
  },
  props: {
    /**
     * @description Сумма в копейках.
     */
    amountInKop: { default: 0, type: Number },
    columns: { required: true, type: Array },
    /**
     * @description Деактивирована.
     * @type {boolean}
     */
    disabled: { default: true, type: Boolean },
    /**
     * @description По полису ДМС.
     * @type {boolean}
     */
    initialByVhiPolicy: { default: false, type: Boolean },
    /**
     * @description Стоимость в копейках.
     * @type {number}
     */
    initialCost: { default: 0, type: Number },
    /**
     * @description Количество.
     * @type {number}
     */
    initialCount: { default: 0, type: Number },
    /**
     * @description По гарантии.
     * @type {boolean}
     */
    initialComplaint: { type: Boolean, default: false },
    /**
     * @description Показывать ячейку с чекбоксом гарантии.
     * @type {boolean}
     */
    showComplaintCell: { type: Boolean, default: true },
    /**
     * @description Скидка.
     * @type {number}
     */
    initialDiscount: { required: true, type: Number },
    /**
     * @description Тип скидки позиции.
     * @type {string}
     */
    initialDiscountType: { required: true, type: String },
    /**
     * @description Показывать постфикс скидки.
     * @type {boolean}
     */
    showDiscountPostfix: { type: Boolean, default: false },
    /**
     * @description Итоговая стоимость.
     */
    initialFinalPrice: { required: true, type: Number },
    /**
     * @description Идентификатор услуги из прайса.
     * @type {number}
     */
    initialServiceId: { default: -1, type: Number },
    /**
     * @description Название услуги.
     */
    initialServiceName: { default: '', type: String },
    /**
     * @description Название категории.
     */
    initialCategoryName: { default: '', type: String },
    /**
     * @description Нарушения ограничений.
     * @type {string[]}
     */
    inputErrors: { default: () => [], type: Array },
    /**
     * @description Список услуг.
     * @type {any[]}
     */
    services: { type: Array, required: true },
    /**
     * @description Статус.
     * @type {string}
     */
    status: { type: String, required: true },
    /**
     * @description НДС.
     * @type {string}
     */
    initialVat: { type: String, required: true },
    /**
     * @description Признак расчета.
     * @type {string}
     */
    initialItemType: { type: String, required: true },
    /**
     * @description Код клиники.
     * @type {string}
     */
    initialServiceClinicCode: { type: String, required: true },
    /**
     * @description Код номенклатуры.
     * @type {string}
     */
    initialServiceDentalCode: { type: String, required: true },
  },
};
</script>
